/**
 * CloudSafe Asset Compliance Report
 *
 * Generates a branded PDF that mirrors the Showcase "Act 4 — Reports" visual:
 *  - Navy cover band with brand mark + meta rows
 *  - Executive summary cells
 *  - Segmented compliance bar with legend
 *  - Recent test activity table
 *  - Audit-trail footer with unique report ID
 *
 * Public API:
 *   generateAssetReportPDF({ asset, assetType, propertyName, history, fileName })
 *   generateSampleReportPDF()  // used by the /showcase demo
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/* ── Brand palette (mirrors the Showcase CSS) ─────────────── */
const C = {
  navy:      [12, 67, 124],
  navyDeep:  [7, 42, 85],
  teal:      [13, 148, 136],
  tealDeep:  [11, 122, 112],
  tealTint:  [232, 249, 247],
  amber:     [217, 119, 6],
  amberTint: [255, 251, 235],
  red:       [220, 38, 38],
  redTint:   [254, 242, 242],
  ink:       [15, 23, 42],
  ink2:      [51, 65, 85],
  muted:     [100, 116, 139],
  hairline:  [228, 232, 238],
  surface2:  [247, 249, 252],
  coverWash: [13, 148, 136], // teal for the radial wash
  white:     [255, 255, 255],
};

/* ── Helpers ──────────────────────────────────────────────── */
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateLong(d) {
  const dt = d ? new Date(d) : new Date();
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

function makeReportId(asset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const seed = `${asset?.reference_id || asset?.id || 'NA'}`
    .toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 14);
  const serial = String(Math.floor(Math.random() * 9000) + 1000);
  return `#CS-${y}-${m}-${seed}-${serial}`;
}

function summarise(history) {
  const total = history.length;
  let passed = 0, failed = 0, pending = 0;
  history.forEach(r => {
    const v = (r.test_result || r.status || '').toString().toLowerCase();
    if (v === 'pass' || v === 'passed') passed++;
    else if (v === 'fail' || v === 'failed') failed++;
    else pending++;
  });
  return { total, passed, failed, pending };
}

const TYPE_LABEL = {
  anchor_point:   'Anchor Point',
  cradle:         'Cradle',
  abseil_system:  'Abseil System',
  ladder:         'Ladder',
  handrail:       'Handrail',
  static_line:    'Static Line',
  walkway:        'Walkway',
  fixed_equipment:'Fixed Equipment',
};

/* ── Main generator ──────────────────────────────────────── */
export function generateAssetReportPDF({
  asset = {},
  assetType = 'asset',
  propertyName = 'Property',
  history = [],
  fileName,
} = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();   // 210
  const pageH = doc.internal.pageSize.getHeight();  // 297

  const reportId = makeReportId(asset);
  const assetName =
    asset.name || asset.system_name || asset.walkway_name || asset.cradle_name ||
    asset.ladder_id || asset.handrail_name || `${TYPE_LABEL[assetType] || 'Asset'} #${asset.id || ''}`;
  const assetCode = asset.reference_id || asset.external_ref || '';
  const s = summarise(history);

  /* ── COVER BAND ──────────────────────────────────────── */
  const coverH = 72;
  doc.setFillColor(...C.navy);
  doc.rect(0, 0, pageW, coverH, 'F');

  // Teal-ish glow in the top-right corner — three overlapping solid circles
  // approximates the radial wash without relying on GState alpha.
  doc.setFillColor(16, 116, 106); // navy-teal mix
  doc.circle(pageW + 10, -10, 56, 'F');
  doc.setFillColor(13, 95, 92);
  doc.circle(pageW + 24, 6, 44, 'F');

  // Brand mark
  const markX = 16, markY = 14;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(markX, markY, 10, 10, 1.8, 1.8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...C.navy);
  doc.text('CS', markX + 5, markY + 6.5, { align: 'center' });

  doc.setTextColor(...C.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('CloudSafe', markX + 14, markY + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(215, 229, 245);
  doc.text('COMPLIANCE REPORT', markX + 14, markY + 9.5);

  // Meta rows
  const metaRows = [
    { k: 'PREPARED FOR', v: `${propertyName}${assetName ? ` · ${assetName}` : ''}` },
    { k: 'GENERATED',    v: fmtDateLong() },
    { k: 'REPORT ID',    v: reportId, mono: true },
  ];
  let metaY = 36;
  metaRows.forEach((row, i) => {
    if (i < metaRows.length - 1) {
      // Hairline under row — faint line via colour choice
      doc.setDrawColor(40, 90, 150);
      doc.setLineWidth(0.2);
      doc.line(16, metaY + 7.5, pageW - 16, metaY + 7.5);
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(160, 190, 220);
    doc.text(row.k, 16, metaY + 3.5);

    doc.setFont(row.mono ? 'courier' : 'helvetica', 'bold');
    doc.setFontSize(row.mono ? 10 : 11);
    doc.setTextColor(...C.white);
    doc.text(String(row.v), pageW - 16, metaY + 4.5, { align: 'right' });
    metaY += 11;
  });

  /* ── BODY ────────────────────────────────────────────── */
  let y = coverH + 12;
  const left = 16;
  const contentW = pageW - 32;

  const sectionHeading = (text) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text(text.toUpperCase(), left, y);
    y += 4;
  };

  // ── Executive summary ──
  sectionHeading('Executive summary');
  const cellW = (contentW - 6) / 4;
  const cellH = 18;
  const cells = [
    { label: 'Tests',   value: s.total,   tint: C.surface2,  valColor: C.ink,      borderColor: C.hairline },
    { label: 'Passed',  value: s.passed,  tint: C.tealTint,  valColor: C.tealDeep, borderColor: [176, 226, 219] },
    { label: 'Pending', value: s.pending, tint: C.amberTint, valColor: C.amber,    borderColor: [248, 217, 176] },
    { label: 'Failed',  value: s.failed,  tint: C.redTint,   valColor: C.red,      borderColor: [251, 202, 202] },
  ];
  cells.forEach((c, i) => {
    const x = left + i * (cellW + 2);
    doc.setFillColor(...c.tint);
    doc.roundedRect(x, y, cellW, cellH, 2, 2, 'F');
    doc.setDrawColor(...c.borderColor);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, cellW, cellH, 2, 2, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...c.valColor);
    doc.text(String(c.value), x + 4, y + 11);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...C.muted);
    doc.text(c.label.toUpperCase(), x + 4, y + 15.5);
  });
  y += cellH + 6;

  // ── Compliance bar ──
  const barW = contentW;
  const barH = 4;
  const tested = Math.max(1, s.total);
  const passedW  = (s.passed  / tested) * barW;
  const pendingW = (s.pending / tested) * barW;
  const failedW  = (s.failed  / tested) * barW;

  // Track
  doc.setFillColor(...C.hairline);
  doc.roundedRect(left, y, barW, barH, barH / 2, barH / 2, 'F');
  // Segments
  let segX = left;
  if (passedW > 0.5) {
    doc.setFillColor(...C.teal);
    doc.roundedRect(segX, y, passedW, barH, barH / 2, barH / 2, 'F');
    segX += passedW;
  }
  if (pendingW > 0.5) {
    doc.setFillColor(...C.amber);
    doc.rect(segX, y, pendingW, barH, 'F');
    segX += pendingW;
  }
  if (failedW > 0.5) {
    doc.setFillColor(...C.red);
    doc.rect(segX, y, failedW, barH, 'F');
  }
  y += barH + 5;

  // Legend
  const pct = (n) => s.total ? Math.round((n / s.total) * 100) : 0;
  const legend = [
    { c: C.teal,  label: `Compliant ${pct(s.passed)}%` },
    { c: C.amber, label: `Pending ${pct(s.pending)}%` },
    { c: C.red,   label: `Failed ${pct(s.failed)}%` },
  ];
  let legX = left;
  legend.forEach((l) => {
    doc.setFillColor(...l.c);
    doc.circle(legX + 1.4, y - 1.2, 1, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C.ink2);
    doc.text(l.label, legX + 4.2, y);
    legX += doc.getTextWidth(l.label) + 12;
  });
  y += 10;

  // ── Asset details card ──
  sectionHeading('Asset');
  const detailRows = [
    ['Asset',           assetName],
    ['Reference',       assetCode || '—'],
    ['Type',            TYPE_LABEL[assetType] || assetType],
    ['Location',        asset.location || asset.location_description || '—'],
    ['Installed',       fmtDate(asset.date_installed)],
    ['Last inspection', fmtDate(asset.last_inspection_date)],
    ['Next inspection', fmtDate(asset.next_inspection_date)],
    ['Capacity',        asset.capacity ? `${asset.capacity} kN` : '—'],
  ];
  const detailsRowH = 6;
  const detailsH = 6 + Math.ceil(detailRows.length / 2) * detailsRowH;
  doc.setDrawColor(...C.hairline);
  doc.setLineWidth(0.3);
  doc.roundedRect(left, y, contentW, detailsH, 2, 2, 'S');

  const colW = contentW / 2;
  detailRows.forEach((row, i) => {
    const col = i % 2;
    const rowI = Math.floor(i / 2);
    const rx = left + 4 + col * colW;
    const ry = y + 6 + rowI * detailsRowH;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.text(row[0].toUpperCase(), rx, ry);
    doc.setFont(row[0] === 'Reference' ? 'courier' : 'helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...C.ink);
    const vx = rx + 28;
    // Clip/truncate long values to the column width
    let v = String(row[1] || '—');
    const maxW = colW - 32;
    if (doc.getTextWidth(v) > maxW) {
      while (v.length > 3 && doc.getTextWidth(v + '…') > maxW) v = v.slice(0, -1);
      v = v + '…';
    }
    doc.text(v, vx, ry);
  });
  y += detailsH + 8;

  // ── Recent test activity ──
  sectionHeading('Recent test activity');

  const tableRows = (history.length ? history : [{
    test_date: null, test_type: 'No tests logged yet', test_result: 'Pending',
    test_load_applied: null,
  }]).slice(0, 18).map(r => [
    assetCode || '—',
    asset.location || asset.location_description || '—',
    fmtDate(r.test_date),
    r.test_load_applied != null ? `${r.test_load_applied} kN` : (r.test_type || '—'),
    (r.test_result || r.status || 'Pending'),
  ]);

  autoTable(doc, {
    startY: y,
    head: [['Asset', 'Location', 'Last test', 'Load / Type', 'Result']],
    body: tableRows,
    theme: 'plain',
    styles: {
      font: 'helvetica',
      overflow: 'linebreak',
    },
    headStyles: {
      textColor: C.muted,
      fillColor: [255, 255, 255],
      fontSize: 7.5,
      fontStyle: 'bold',
      cellPadding: { top: 2, bottom: 3, left: 2, right: 2 },
      lineColor: C.hairline,
      lineWidth: { bottom: 0.3 },
    },
    bodyStyles: {
      fontSize: 8.5,
      textColor: C.ink2,
      cellPadding: { top: 2.4, bottom: 2.4, left: 2, right: 2 },
      lineColor: [241, 244, 248],
      lineWidth: { bottom: 0.2 },
    },
    columnStyles: {
      0: { font: 'courier', textColor: C.ink, fontStyle: 'bold', cellWidth: 42 },
      3: { cellWidth: 32 },
      4: { cellWidth: 32, fontStyle: 'bold' },
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) {
        const v = String(data.cell.raw || '').toLowerCase();
        if (v === 'pass' || v === 'passed') {
          data.cell.styles.textColor = C.tealDeep;
          data.cell.styles.fillColor = C.tealTint;
        } else if (v === 'fail' || v === 'failed') {
          data.cell.styles.textColor = C.red;
          data.cell.styles.fillColor = C.redTint;
        } else {
          data.cell.styles.textColor = C.amber;
          data.cell.styles.fillColor = C.amberTint;
        }
      }
    },
    margin: { left, right: left },
  });

  const tableEndY = doc.lastAutoTable ? doc.lastAutoTable.finalY : y;
  y = tableEndY + 14;

  /* ── Footer band ──────────────────────────────────── */
  const footY = pageH - 18;
  doc.setDrawColor(...C.hairline);
  doc.setLineWidth(0.3);
  doc.line(left, footY - 6, pageW - left, footY - 6);

  // Teal tick mark as a simple "signed" glyph
  doc.setDrawColor(...C.teal);
  doc.setLineWidth(0.9);
  doc.line(left, footY - 2.8, left + 1.6, footY - 1.4);
  doc.line(left + 1.6, footY - 1.4, left + 4, footY - 4.5);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...C.muted);
  doc.text('VERIFIED EXPORT', left + 7, footY - 3.5);

  doc.setFont('courier', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.ink);
  doc.text(`${reportId} · logged to audit trail`, left + 7, footY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...C.muted);
  doc.text('Page 1 of 1', pageW - left, footY, { align: 'right' });

  /* ── Save ─────────────────────────────────────────── */
  const safeName = (str) => (str || 'asset').toString().replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
  const defaultName = `CloudSafe_${safeName(propertyName)}_${safeName(assetName)}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName || defaultName);
  return reportId;
}

/* ── Sample report used by the Showcase "Download sample PDF" button ── */
export function generateSampleReportPDF() {
  const sampleHistory = [
    { test_date: '2026-04-23', test_type: 'Proof Load', test_result: 'Pass', test_load_applied: 6.2 },
    { test_date: '2025-04-11', test_type: 'Proof Load', test_result: 'Pass', test_load_applied: 6.0 },
    { test_date: '2024-04-02', test_type: 'Proof Load', test_result: 'Pass', test_load_applied: 6.1 },
    { test_date: '2023-04-04', test_type: 'Proof Load', test_result: 'Pass', test_load_applied: 6.0 },
    { test_date: '2022-03-29', test_type: 'Proof Load', test_result: 'Pass', test_load_applied: 6.0 },
    { test_date: '2021-03-30', test_type: 'Proof Load', test_result: 'Pass', test_load_applied: 5.9 },
  ];

  return generateAssetReportPDF({
    asset: {
      id: 121,
      reference_id: 'UCL44-AS-001',
      name: 'Davit Sockets 001',
      location_description: 'ROOF · Level 4 · North',
      date_installed: '2015-06-01',
      last_inspection_date: '2026-04-23',
      next_inspection_date: '2027-04-23',
      capacity: 6.0,
    },
    assetType: 'abseil_system',
    propertyName: 'UCL East',
    history: sampleHistory,
    fileName: 'CloudSafe_Sample_Compliance_Report.pdf',
  });
}
